import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import CardParcelModalWindow from '../modals/CardParcelModalWindow/CardParcelModalWindow';
import cn from 'classnames';
import DataTable, { DataViewHandle } from '@containers/DataTable';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { formatDate, formatSumWithCurrency } from '@utils/formatValues';
import { getCurrentLanguage } from '@utils/lang';
import { getProcessing, TProcessingParcel } from '@api/apiClient/returns';
import { IProcessingTabProps } from '../types';
import { Modal } from '@components/Modal';
import { Row, Column } from '@components/Grid';
import { sortByPrice, sortByStores } from '@utils/sorting';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes.ts';
import { Text, TextColor, TextSize } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import { useProfile } from '@contexts/ProfileContext';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const ProcessingTab: React.FC<IProcessingTabProps> = (props: IProcessingTabProps): React.ReactElement => {
  const {
    isFilterOpen,
    setIsFilterOpen,
    // onUpdate
  } = props;

  const [columnsSort, setColumnsSort] = useState<string[]>([]);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [parcel, setParcel] = useState<TProcessingParcel | undefined>(undefined);
  const dataRef = useRef<DataViewHandle>(null);

  const currentLanguage = getCurrentLanguage();
  const { account } = useProfile();
  const { t } = useTranslation();

  const variableCurrency: string = account?.region === 'TR' ? '$' : '¥';

  // const refreshData = () => {
  //   if (dataRef.current?.refresh) {
  //     dataRef.current.refresh();
  //   }
  //   onUpdate();
  // };

  const onCloseParcel = () => {
    setIsOpenParcelWindow(false);
  };

  const handlerSortByPrice = (position: number, value: string, filters: FilterModel[]): void => {
    sortByPrice({ columnsSort, filters, position, setColumnsSort, setInnerFilters, value });
  };

  const handlerSortByStores = (position: number, value: string, filters: FilterModel[]): void => {
    sortByStores({ columnsSort, filters, position, setColumnsSort, setInnerFilters, value });
  };

  const columns: TableColumn<TProcessingParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TProcessingParcel>) => (
        <TrackingNumber
          externalId={row.data?.trackingNumber}
          trackNumber={row.data?.directStreamExternalId}
          onClick={() => {
            setIsOpenParcelWindow(true);
            setParcel(row.data);
          }}
          allowEqualNumbers
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: "200px"
    },
    {
      header: t('returnsPage.processingTab.name'),
      accessor: (row: TableRowType<TProcessingParcel>) => (
          <a
              className={styles.link}
              href={row?.data?.url}
              rel="noreferrer"
              target="_blank"
          >
            {row?.data?.title}
          </a>
      ),
      width: '235px'
    },
    {
      header: t('returnsPage.processingTab.priceOfProduct'),
      accessor: (row: TableRowType<TProcessingParcel>) => (
          <div className={styles.price}>
            <Text
                color={TextColor.PRIMARY}
                size={TextSize.S}
            >
              {formatSumWithCurrency(row.data.priceRub, '₽')}
            </Text>
            <Text
                color={TextColor.DARK_MUTED}
                size={TextSize.XS}
            >
              {formatSumWithCurrency(row.data.priceCny, variableCurrency)}
            </Text>
          </div>
      ),
      alignment: 'right',
      noOverflow: true,
      sortable: true,
      sortableFn: handlerSortByPrice,
      width: '100px'
    },
    {
      header: t('returnsPage.processingTab.store'),
      accessor: (row: TableRowType<TProcessingParcel>) => (
        <AvatarWithMarketplace
          id={row.data.seller.id}
          name={row.data.seller.name}
          marketplace={row.data.marketplaceType}
        />
      ),
      alignment: 'center',
      noOverflow: true,
      sortable: true,
      sortableFn: handlerSortByStores
    },
    {
      header: t('returnsPage.processingTab.status'),
      accessor: (row: TableRowType<TProcessingParcel>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t(`returnsPage.processingTab.statuses.${row.data.status}`)}
          </Text>
      )
    },
    {
      header: t('returnsPage.processingTab.date'),
      accessor: (row: TableRowType<TProcessingParcel>) => formatDate(row.data.createdAt, 'dd.MM.yy'),
      alignment: 'right',
      type: TableColumnType.TERTIARY
    }
  ], [currentLanguage, columnsSort]);

  useEffect(() => {
    const columnsSort: string[] = columns.map((item: TableColumn<TProcessingParcel>) => item.sortable ? 'asc' : '');
    setColumnsSort(columnsSort);
  }, []);

  return (
      <div>
        <Box pb={5}>
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t('returnsPage.processingTab.info_1')}
          </Text>
        </Box>
        <Box pb={5}>
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t('returnsPage.processingTab.info_2')}
          </Text>
        </Box>
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="returns-processing"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'trackNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('filters.searchByTrackNumber')
                    },
                    {
                      name: 'status',
                      type: FilterTypes.STATUS,
                      placeholder: t('returnsPage.processingTab.status')
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    },
                    // {
                    //   name: "marketplaceType",
                    //   type: FilterTypes.MARKETPLACE,
                    // },
                    {
                      name: 'dateRange',
                      type: FilterTypes.DATE_RANGE,
                      placeholder: t('returnsPage.processingTab.date'),
                      fieldNames: ['startDate', 'endDate']
                    }
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        <DataTable<TProcessingParcel>
            columns={columns}
            columnsSort={columnsSort}
            dataRef={dataRef}
            defaultPageSize={10}
            filters={innerFilters}
            getData={getProcessing}
            notFound={t('returnsPage.processingTab.noParcels')}
            pending={!isInitialized}
            queryKey="processingParcels"
        />
        <Modal
            bodyWithoutIndentations
            isOpen={isOpenParcelWindow}
            onClickOverlay={onCloseParcel}
            onClose={onCloseParcel}
            width={900}
        >
          <CardParcelModalWindow
              callFrom="processingTab"
              handlerSetIsOpenParcelWindow={setIsOpenParcelWindow}
              parcel={parcel!}
          />
        </Modal>
      </div>
  );
};

export default ProcessingTab;
